import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Phone, MessageCircle, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../data/services';
import axios from 'axios';

export const HeroSection = () => {
  const [promoImages, setPromoImages] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const defaultImage = "/assets/hero-default.png";

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const BACKEND_URL = rawBackendUrl.replace(/\/$/, '');
        const API = `${BACKEND_URL}/api`;
        const response = await axios.get(`${API}/settings`);

        let images = [defaultImage];

        if (response.data) {
          // Use admin-configured default image if available
          const adminDefault = response.data.default_hero_image;
          const heroDefault = (adminDefault && adminDefault !== 'null') ? adminDefault : defaultImage;

          if (response.data.promotions_list) {
            try {
              const list = JSON.parse(response.data.promotions_list);
              const now = new Date();
              const activePromos = list
                .filter(p => !p.expiry || new Date(p.expiry) > now)
                .map(p => {
                  const url = p.url;
                  if (url.startsWith('data:') || url.startsWith('http')) return url;
                  return url.startsWith('/') ? BACKEND_URL + url : BACKEND_URL + '/' + url;
                });

              images = activePromos.length > 0 ? [...activePromos, heroDefault] : [heroDefault];
            } catch (e) {
              console.error("Failed to parse promotions list", e);
              images = [heroDefault];
            }
          } else {
            images = [heroDefault];
          }
        }
        setPromoImages(images);
      } catch (error) {
        console.error('Failed to fetch hero promotion:', error);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    if (promoImages.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % promoImages.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [promoImages]);

  const heroImage = promoImages[currentIndex] || defaultImage;
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden hero-pattern" data-testid="hero-section">
      {/* Background Decorative Elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-blue-600/10 rounded-full blur-2xl" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-8"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-blue-100 text-blue-600 px-4 py-2 rounded-full text-sm font-medium"
            >
              <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              Trusted by 1000+ Customers
            </motion.div>

            {/* Heading */}
            <div className="space-y-4">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight tracking-tight">
                Venkateswara Hospitality &
                <span className="block text-slate-800 leading-none">
                  Facilities Management services Private Limited
                  <span className="text-blue-600 ml-2">(VHFMS)</span>
                </span>
                <span className="block text-blue-600 text-2xl sm:text-3xl lg:text-4xl mt-4">At Your Fingertips.</span>
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-lg md:text-xl font-semibold text-slate-700 pb-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={24} className="text-blue-500" />
                  <span>Trusted</span>
                </div>
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300 hidden md:block" />
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={24} className="text-blue-500" />
                  <span>Professional</span>
                </div>
                <div className="w-1.5 h-1.5 rounded-full bg-slate-300 hidden md:block" />
                <div className="flex items-center gap-2">
                  <CheckCircle2 size={24} className="text-blue-500" />
                  <span>Affordable</span>
                </div>
              </div>
              <p className="text-lg text-slate-600 max-w-xl">
                Simplify your home life with expert services delivered right to your doorstep. From deep cleaning and repairs to complete home maintenance, we provide reliable solutions tailored to your needs.With our service-on-demand approach, booking is quick, service is fast, and quality is guaranteed. Sit back, relax, and let our professionals take care of everything.
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                className="group flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-8 py-4 rounded-full font-semibold text-base transition-colors"
                data-testid="hero-explore-btn"
              >
                Explore Services
                <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>

              <a
                href={`https://wa.me/${contactInfo.whatsapp}?text=Hi, I would like to inquire about your services.`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-8 py-4 rounded-full font-semibold text-base transition-colors"
                data-testid="hero-whatsapp-btn"
              >
                <MessageCircle size={20} />
                WhatsApp Us
              </a>
            </div>

            {/* Quick Contact */}
            <div className="flex items-center gap-6 pt-4">
              <a
                href={`tel:+91${contactInfo.phone}`}
                className="flex items-center gap-3 text-slate-600 hover:text-blue-500 transition-colors"
                data-testid="hero-phone-link"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Phone size={20} className="text-blue-500" />
                </div>
                <div className="text-left">
                  <p className="text-xs text-slate-400">Call us anytime</p>
                  <p className="font-semibold">+91 {contactInfo.phone}</p>
                </div>
              </a>
            </div>
          </motion.div>

          {/* Right Image */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative"
          >
            <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-slate-50">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentIndex}
                  src={heroImage}
                  alt="VHFMS Professional Services"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.8 }}
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />

              {/* Floating Stats Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl p-4 shadow-lg"
              >
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-blue-500">15+</p>
                    <p className="text-xs text-slate-500">Services</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-500">1K+</p>
                    <p className="text-xs text-slate-500">Happy Clients</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-blue-500">4.7</p>
                    <p className="text-xs text-slate-500">Avg Rating</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-500 rounded-2xl -z-10 rotate-12" />
            <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-yellow-400 rounded-xl -z-10 -rotate-6" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
